import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AuthApi } from '@/api/auth.api';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

function base64Decode(str: string) {
  try {
    return decodeURIComponent(escape(window.atob(str)));
  } catch {
    return '';
  }
}

const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const code = params.get('code');
    if (!ref || !code) {
      setStatus('error');
      return;
    }
    const email = base64Decode(ref);
    const resetPasswordCode = base64Decode(code);
    if (!email || !resetPasswordCode) {
      setStatus('error');
      return;
    }
    AuthApi.initiateResetPassword(email, resetPasswordCode)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 via-white to-blue-200 px-4 py-12 sm:px-6 lg:px-8 transition-colors duration-500">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-2xl rounded-3xl p-10 flex flex-col items-center relative border border-blue-100">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4 shadow-sm">
            <Mail className="text-blue-600 w-8 h-8" />
          </div>
          <h2 className="text-center text-2xl font-extrabold mb-2 text-gray-800 tracking-tight">Email Verification</h2>
          <p className="text-center text-gray-500 mb-8 text-base">We are verifying your email address. Please wait a moment.</p>
          {status === 'pending' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="animate-spin text-blue-500 w-8 h-8 mb-2" />
              <span className="text-blue-600 font-medium">Verifying your email...</span>
            </div>
          )}
          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-2 shadow-sm">
                <CheckCircle2 className="text-green-600 w-8 h-8" />
              </div>
              <div className="text-green-700 font-semibold text-lg text-center">Email successfully verified!</div>
              <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md" onClick={() => navigate('/auth')}>Back to Login</Button>
            </div>
          )}
          {status === 'error' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-2 shadow-sm">
                <XCircle className="text-red-600 w-8 h-8" />
              </div>
              <div className="text-red-700 font-semibold text-lg text-center">Email verification failed.</div>
              <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md" onClick={() => navigate('/auth')}>Back to Login</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail; 